import { Box, styled, SxProps, Theme } from '@mui/material';
import classNames from 'classnames';
import { type CSSProperties, type ReactNode, useRef } from 'react';
import { useResizeObserver } from 'usehooks-ts';

import { carouselClasses } from './Carousel';

interface RootProps {
  itemWidth: number;
  itemHeight: number;
  gap: number;
  normalSize: number;
  normalStretch: boolean;
  normalMinSize?: number;
  isHorizontal?: boolean;
  isFullHeight: boolean;
}

const excludedRootProps: PropertyKey[] = [
  'itemWidth',
  'itemHeight',
  'gap',
  'normalSize',
  'normalStretch',
  'normalMinSize',
  'isHorizontal',
  'isFullHeight',
];

const Root = styled(Box, {
  shouldForwardProp: prop => !excludedRootProps.includes(prop),
})<RootProps>(
  ({
    theme: { spacing },
    itemWidth,
    itemHeight,
    gap,
    normalSize,
    normalStretch,
    normalMinSize,
    isHorizontal = true,
    isFullHeight,
  }) => ({
    display: 'flex',
    fontFamily: "'JetBrains Mono', monospace",
    maxHeight: '100%',
    width: '100%',

    [`.${carouselClasses.header}`]: {
      writingMode: 'sideways-lr',
      display: 'flex',
      justifyContent: 'end',
      padding: spacing(2, 1),
    },

    [`& .${carouselClasses.carousel}`]: {
      display: 'flex',
      width: '100%',
      gap: 8,
      position: 'relative',
      overflow: 'hidden',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      '&::-webkit-scrollbar': { display: 'none' },

      boxSizing: 'border-box',
      ...(isHorizontal
        ? { flexDirection: 'column', flex: 1 }
        : { flexDirection: 'row', flex: `0 0 ${itemWidth}px` }),
      minHeight: 0,
      padding: 0,

      [`.${carouselClasses.carouselContent}`]: {
        display: 'grid',

        // --- orientation branches ---
        ...(isHorizontal
          ? {
              // HORIZONTAL
              gridTemplateRows: `repeat(${normalSize}, minmax(${itemHeight}px, 1fr))`,
              gridTemplateColumns: `repeat(auto-fill, ${itemWidth}px)`,
              gridAutoColumns: `${itemWidth}px`,
              gridAutoFlow: 'column',
              touchAction: 'pan-x pinch-zoom',
            }
          : {
              // VERTICAL
              gridTemplateRows: `repeat(${normalSize}, minmax(${itemHeight}px, 1fr))`,
              gridAutoRows: `${itemHeight}px`,
              gridTemplateColumns: `${itemWidth}px`,
              gridAutoFlow: 'row',
              touchAction: 'pan-y pinch-zoom',
            }),

        overscrollBehavior: 'contain',

        columnGap: `${gap}px`,
        rowGap: `${gap}px`,
        position: 'relative',
        padding: 0,
        margin: 0,
        listStyle: 'none',

        // Keep native scroll for both modes
        ...(isHorizontal
          ? { overflowX: 'auto', overscrollBehaviorX: 'contain' }
          : { overflowY: 'auto', overscrollBehaviorY: 'contain' }),
        scrollBehavior: 'smooth',
        transform: `translate3d(0,0,0)`,
        WebkitOverflowScrolling: 'touch',

        scrollbarWidth: 'none',
        scrollbarHeight: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        ...(isFullHeight ? { height: '100%' } : {}),
        width: '100%',

        [`.${carouselClasses.carouselItem}`]: {
          width: `${itemWidth}px`,
          height: `${itemHeight}px`,
          boxSizing: 'border-box',
          minWidth: 0,
          minHeight: 0,
        },

        ...(normalStretch &&
          (isHorizontal
            ? {
                gridTemplateRows: `repeat(${normalSize}, minmax(${Math.floor(normalMinSize ?? itemHeight)}px, 1fr))`,
                [`.${carouselClasses.carouselItem}`]: {
                  height: '100%',
                  minHeight: 0,
                  '& > *': {
                    width: '100%',
                    height: '100%',
                    boxSizing: 'border-box',
                    minWidth: 0,
                    minHeight: 0,
                  },
                },
              }
            : {
                height: '100%',
                gridTemplateRows: `repeat(${normalSize}, minmax(${Math.floor(normalMinSize ?? itemHeight)}px, 1fr))`,
                [`.${carouselClasses.carouselItem}`]: {
                  height: '100%',
                  minHeight: 0,
                  '& > *': {
                    width: '100%',
                    height: '100%',
                    boxSizing: 'border-box',
                    minWidth: 0,
                    minHeight: 0,
                  },
                },
              })),
      },
    },
  }),
);

interface CarouselStaticProps<T> {
  items: ReadonlyArray<T>;
  renderItem: (item: T) => ReactNode;
  itemKey: keyof T;

  /**
   * Reflects the spread or width across the non-dominant axis.
   * In horizontal mode, this represents the number of rows.
   * In vertical mode, this represents the number of columns.
   * @default 1
   */
  normalSize?: number;

  /**
   * Minimum row/column size in pixels when normalStretch is enabled.
   */
  normalMinSize?: number;

  /**
   * Width in horizontal mode, or height in vertical mode, of each item in pixels.
   * @default 300
   */
  itemWidth?: number;

  /**
   * Height of each item in pixels.
   * @default 140
   */
  itemHeight?: number;

  /**
   * Gap between items in pixels.
   * @default 8
   */
  gap?: number;

  /**
   * Callback triggered on container resize.
   */
  onResize?: (el: HTMLUListElement) => void;

  /**
   * Custom styles for the root container.
   */
  sx?: SxProps<Theme> | undefined;

  /**
   * When true, rows/columns expand to fill the container height.
   * This requires that the container has a defined height.
   * @default false
   */
  normalStretch?: boolean;

  /**
   * Layout orientation.
   * @default 'horizontal'
   */
  variant?: 'vertical' | 'horizontal';

  /**
   * When true, content fills 100% of container height.
   * @default true
   */
  isFullHeight?: boolean;
}

/**
 * CarouselStatic - Lightweight static carousel component
 *
 * Renders items in a scrollable grid layout with the same styles as Carousel,
 * but without animations, pagination, or auto-scroll functionality.
 * Perfect for preview modes and static content displays.
 */
// eslint-disable-next-line comma-spacing
export const CarouselStatic = <T,>({
  items,
  renderItem,
  itemKey,
  normalSize = 1,
  itemWidth = 300,
  itemHeight = 140,
  gap = 8,
  onResize,
  sx,
  normalStretch = false,
  normalMinSize,
  variant = 'horizontal',
  isFullHeight = true,
}: CarouselStaticProps<T>) => {
  const ulRef = useRef<HTMLUListElement>(null);

  const safeNormal = Math.max(
    1,
    Number.isFinite(normalSize) ? Math.floor(normalSize) : 1,
  );

  const isHorizontal = variant === 'horizontal';

  useResizeObserver({
    ref: ulRef as React.RefObject<HTMLElement>,
    onResize: () => {
      if (ulRef.current) {
        onResize?.(ulRef.current);
      }
    },
  });

  return (
    <Root
      sx={sx}
      gap={gap}
      // Floor, never round: itemHeight can be a fractional exact-fit value
      // and feeds the grid row MINIMUM (minmax). Rounding up makes the sum of
      // row minimums exceed the container and clips the bottom row (TMDXNC-18).
      itemHeight={Math.floor(itemHeight)}
      itemWidth={Math.round(itemWidth)}
      normalSize={safeNormal}
      normalStretch={normalStretch}
      normalMinSize={normalMinSize}
      isHorizontal={isHorizontal}
      isFullHeight={isFullHeight}
    >
      <Box className={classNames(carouselClasses.carousel)}>
        <ul
          ref={ulRef}
          className={classNames(carouselClasses.carouselContent)}
          style={
            {
              ...(normalMinSize
                ? { ['--carousel-row-min']: `${normalMinSize}px` }
                : {}),
            } as CSSProperties
          }
        >
          {items.map((item, index) => (
            <li
              key={String(item?.[itemKey] ?? `item-${index}`)}
              className={classNames(carouselClasses.carouselItem)}
              data-rows={safeNormal}
              data-row-index={index % safeNormal}
              data-column={Math.floor(index / safeNormal)}
            >
              {item && renderItem(item)}
            </li>
          ))}
        </ul>
      </Box>
    </Root>
  );
};
